import { useMemo } from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck, AlertTriangle, CheckCircle2, ExternalLink, Activity,
} from "lucide-react";
import {
  DESIGNER_RULES, CATEGORY_LABEL, CATEGORY_COLOR, type DesignerRule,
} from "../../data/designerRules";
import { getActiveProjectGraph } from "../../data/sampleGraph";
import type { GraphNode } from "../../types/ontology";
import { predicateToText } from "../../lib/predicateRender";
import { cls } from "../../lib/cls";

/* ProjectRuleStatus — cockpit Rules tab, vertical timeline.
 *
 *   one rail · one dot per rule · fired (foyr) above clean (fern)
 *
 * Every row is project-derived (no rule library duplicate, no
 * builder). Designed to read like a clean activity timeline a
 * senior designer scans top-to-bottom at the start of a session. */

interface ProjectRuleStatusProps {
  appliedIds: ReadonlySet<string>;
}

interface RuleRow {
  rule: DesignerRule;
  status: "fired" | "clean";
  alerts: GraphNode[];
  caught: GraphNode[];
}

function findAlertsForRule(ruleId: string): { alerts: GraphNode[]; caught: GraphNode[] } {
  const alertEdges = getActiveProjectGraph().edges.filter((e) => e.kind === "RULE_FIRED_FROM" && e.target === ruleId);
  const alerts = alertEdges
    .map((e) => getActiveProjectGraph().nodes.find((n) => n.id === e.source))
    .filter((n): n is GraphNode => !!n && n.kind === "Alert");

  const caught: GraphNode[] = [];
  const seen = new Set<string>();
  for (const a of alerts) {
    for (const e of getActiveProjectGraph().edges) {
      if (e.source !== a.id || e.kind !== "OF") continue;
      const tgt = getActiveProjectGraph().nodes.find((n) => n.id === e.target);
      if (tgt && !seen.has(tgt.id)) { seen.add(tgt.id); caught.push(tgt); }
    }
  }
  return { alerts, caught };
}

function buildRows(appliedIds: ReadonlySet<string>): RuleRow[] {
  const rows: RuleRow[] = [];
  for (const rule of DESIGNER_RULES) {
    if (!appliedIds.has(rule.id)) continue;
    const { alerts, caught } = findAlertsForRule(rule.id);
    rows.push({
      rule,
      status: alerts.length > 0 ? "fired" : "clean",
      alerts,
      caught,
    });
  }
  rows.sort((a, b) => {
    if (a.status === b.status) return a.rule.title.localeCompare(b.rule.title);
    return a.status === "fired" ? -1 : 1;
  });
  return rows;
}

export function ProjectRuleStatus({ appliedIds }: ProjectRuleStatusProps): JSX.Element {
  const rows = useMemo(() => buildRows(appliedIds), [appliedIds]);
  const fired = rows.filter((r) => r.status === "fired").length;
  const clean = rows.length - fired;
  const project = getActiveProjectGraph().nodes.find((n) => n.kind === "Project");

  return (
    <section className="space-y-4">
      <Header
        projectLabel={project?.label ?? "this project"}
        fired={fired}
        clean={clean}
        total={rows.length}
      />

      {rows.length === 0 ? (
        <EmptyState />
      ) : (
        <Timeline rows={rows} />
      )}
    </section>
  );
}

/* ─── Header ─────────────────────────────────────────────────── */

function Header({ projectLabel, fired, clean, total }: { projectLabel: string; fired: number; clean: number; total: number }): JSX.Element {
  return (
    <div className="panel-hero p-5 sm:p-6 relative overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-30 pointer-events-none" />
      <div className="absolute -top-20 -right-12 w-64 h-64 rounded-full bg-foyr/12 blur-3xl pointer-events-none" />
      <div className="relative flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <span className="w-10 h-10 rounded-xl bg-ink-950 text-cream grid place-items-center shrink-0">
            <ShieldCheck className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <div className="eyebrow">Rules in action · {projectLabel}</div>
            <div className="display-2 text-ink-950 mt-0.5 leading-[1.04]">
              {total === 0 ? (
                <>No rules watching yet.</>
              ) : fired === 0 ? (
                <>All {total} rules <span className="text-fern">clean</span>.</>
              ) : (
                <>
                  <span className="tabular text-foyr">{fired}</span>{" "}
                  <span>fired</span>
                  <span className="text-ink-500"> · </span>
                  <span className="tabular text-fern">{clean}</span>{" "}
                  <span>clean</span>
                </>
              )}
            </div>
            <p className="text-[13px] text-ink-700 mt-1.5 max-w-2xl leading-snug">
              Each row below is one watch the agent re-evaluates every save. Fired rules surface the entity they caught and the reason — no static checklist.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {fired > 0 ? (
            <span className="pill bg-foyr text-cream tabular">
              <AlertTriangle className="w-3 h-3" /> {fired} attention
            </span>
          ) : null}
          {clean > 0 ? (
            <span className="pill bg-fern/10 border border-fern/30 text-fern tabular">
              <CheckCircle2 className="w-3 h-3" /> {clean} clean
            </span>
          ) : null}
          <a href="/library" className="pill bg-cream-dark border border-border text-ink-700 hover:text-ink-950 hover:border-foyr/40 transition">
            <Activity className="w-3 h-3" /> Add more
          </a>
        </div>
      </div>
    </div>
  );
}

/* ─── Timeline ───────────────────────────────────────────────── */

function Timeline({ rows }: { rows: RuleRow[] }): JSX.Element {
  return (
    <ol className="relative pl-7 sm:pl-9">
      {/* The rail */}
      <span
        aria-hidden
        className="absolute left-[11px] sm:left-[15px] top-2 bottom-2 w-px bg-gradient-to-b from-foyr/60 via-border to-fern/40"
      />
      {rows.map((row, i) => (
        <TimelineRow key={row.rule.id} row={row} index={i} last={i === rows.length - 1} />
      ))}
    </ol>
  );
}

function TimelineRow({ row, index, last }: { row: RuleRow; index: number; last: boolean }): JSX.Element {
  const { rule, status, alerts, caught } = row;
  const fired = status === "fired";

  return (
    <motion.li
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay: index * 0.03 }}
      className={cls("relative", last ? "pb-1" : "pb-4")}
    >
      {/* Dot on the rail */}
      <span
        aria-hidden
        className={cls(
          "absolute -left-7 sm:-left-9 top-3 w-5 h-5 sm:w-6 sm:h-6 rounded-full grid place-items-center border-2 bg-cream",
          fired
            ? "border-foyr text-foyr-deep shadow-[0_0_0_4px_rgba(201,92,26,0.10)]"
            : "border-fern text-fern shadow-[0_0_0_4px_rgba(63,122,85,0.10)]",
        )}
      >
        {fired ? <AlertTriangle className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> : <CheckCircle2 className="w-2.5 h-2.5 sm:w-3 sm:h-3" />}
      </span>

      {/* Card */}
      <div className={cls(
        "rounded-2xl border bg-cream px-4 py-3.5 transition-all",
        fired ? "border-foyr/40 shadow-[0_2px_8px_-4px_rgba(201,92,26,0.18)]" : "border-border hover:border-foyr/20",
      )}>
        {/* Title row */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <span className={cls("pill text-[10px] border", CATEGORY_COLOR[rule.category])}>
              {CATEGORY_LABEL[rule.category]}
            </span>
            <span className="text-[14px] font-semibold text-ink-950 leading-tight">
              {rule.title}
            </span>
          </div>
          <span className={cls(
            "pill text-[10px] uppercase tracking-[0.12em] font-mono shrink-0",
            fired
              ? "bg-foyr text-cream"
              : "bg-fern/10 text-fern border border-fern/30",
          )}>
            {fired ? "fired" : "clean"}
          </span>
        </div>

        {/* Plain-English summary */}
        <div className="text-[12.5px] text-ink-700 mt-1.5 leading-snug">{rule.natural}</div>

        {/* Predicate (typed, AST-rendered) */}
        <div className="text-[11px] text-ink-500 mt-1.5 font-mono italic leading-snug">
          {predicateToText(rule.predicate)}
        </div>

        {fired ? <Fired alerts={alerts} caught={caught} because={rule.because} /> : <Clean because={rule.because} />}
      </div>
    </motion.li>
  );
}

function Fired({ alerts, caught, because }: { alerts: GraphNode[]; caught: GraphNode[]; because: string }): JSX.Element {
  return (
    <div className="mt-3 space-y-1.5">
      {alerts.map((a) => (
        <div key={a.id} className="rounded-xl border border-foyr/20 bg-foyr/[0.04] px-3 py-2">
          <div className="flex items-baseline justify-between gap-3">
            <div className="text-[12.5px] text-ink-950 font-semibold leading-tight">{a.label}</div>
            <span className="text-[10px] font-mono uppercase tracking-wider text-ink-500 shrink-0">
              {a.attrs["severity"] === "warn" ? "warn" : a.attrs["severity"] === "info" ? "info" : ""}
            </span>
          </div>
          <div className="text-[11px] text-ink-700 mt-0.5 italic">"{because}"</div>
          {caught.length > 0 ? (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {caught.slice(0, 4).map((c) => (
                <span key={c.id} className="pill bg-cream-dark border border-border text-ink-700 text-[10px]">
                  <span className="font-mono text-[9px] text-ink-500">{c.kind}</span>
                  <span className="truncate max-w-[200px]">{c.label}</span>
                </span>
              ))}
            </div>
          ) : null}
          <button className="mt-2 inline-flex items-center gap-1 text-[11px] text-foyr-deep hover:underline underline-offset-2">
            <ExternalLink className="w-3 h-3" /> Open in cockpit
          </button>
        </div>
      ))}
    </div>
  );
}

function Clean({ because }: { because: string }): JSX.Element {
  return (
    <div className="mt-2 text-[11.5px] text-ink-700 leading-snug">
      <span className="text-ink-500">Watched this project · nothing tripped it. </span>
      <span className="italic">"{because}"</span>
    </div>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="panel p-7 text-center space-y-2">
      <span className="inline-flex w-11 h-11 rounded-full bg-cream-dark border border-border items-center justify-center text-ink-700">
        <ShieldCheck className="w-5 h-5" />
      </span>
      <div className="text-base font-semibold text-ink-950">No rules watching this project yet.</div>
      <p className="text-[13px] text-ink-500 max-w-md mx-auto">
        Open the <a href="/library" className="text-foyr-deep underline underline-offset-2">Rule library</a> and pick a few to apply. Once one is applied, it shows up here on the timeline — fired or clean — with the entity it caught and the reason.
      </p>
    </div>
  );
}
